import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { ChatWidget } from "@/components/ChatWidget";
import { ErrorBoundary, reportError } from "@/components/ErrorBoundary";
import { InstallBanner } from "@/components/InstallBanner";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProductPage from "@/pages/ProductPage";
import Checkout from "@/pages/Checkout";
import PaymentResult from "@/pages/PaymentResult";
import AuthPage from "@/pages/AuthPage";
import Account from "@/pages/Account";
import DietCalculator from "@/pages/DietCalculator";
import HealthGuide from "@/pages/HealthGuide";
import Advisor from "@/pages/Advisor";
import Admin from "@/pages/Admin";
import Diagnosis from "@/pages/Diagnosis";
import Legal from "@/pages/Legal";
import Gear from "@/pages/Gear";
import HorseWeight from "@/pages/HorseWeight";
import Stable from "@/pages/Stable";
import Competitions from "@/pages/Competitions";
import Market from "@/pages/Market";
import Alerts from "@/pages/Alerts";
import Antidoping from "@/pages/Antidoping";
import AppPoster from "@/pages/AppPoster";
import ClubHub from "@/pages/club/ClubHub";
import ClubPage from "@/pages/club/ClubPage";
import CompetitionPage from "@/pages/club/CompetitionPage";
import ClubPayResult from "@/pages/club/ClubPayResult";
import { AuthCallback } from "@/components/GoogleAuth";
import ResetPassword from "@/pages/ResetPassword";
import AdoptList from "@/pages/adopcion/AdoptList";
import AdoptForm from "@/pages/adopcion/AdoptForm";
import AdoptPet from "@/pages/adopcion/AdoptPet";
import AdoptPanel from "@/pages/adopcion/AdoptPanel";
import AdoptChat from "@/pages/adopcion/AdoptChat";
import AdoptRules from "@/pages/adopcion/AdoptRules";
import ListingPage, { ListingForm } from "@/pages/ListingPage";
import { BlogList, BlogPost } from "@/pages/Blog";

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const RouteBoundary = ({ children }) => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    const onErr = (e) => reportError(e.message || "error", e.error?.stack || "");
    window.addEventListener("error", onErr);
    return () => window.removeEventListener("error", onErr);
  }, []);
  return <ErrorBoundary path={pathname}>{hash?.includes("session_id=") ? <AuthCallback /> : children}</ErrorBoundary>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ScrollTop />
            <InstallBanner />
            <Header />
            <main>
              <RouteBoundary>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/tienda" element={<Catalog />} />
                <Route path="/producto/:slug" element={<ProductPage />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/pedido/resultado" element={<PaymentResult />} />
                <Route path="/login" element={<AuthPage mode="login" />} />
                <Route path="/registro" element={<AuthPage mode="register" />} />
                <Route path="/cuenta" element={<Account />} />
                <Route path="/calculadora" element={<DietCalculator />} />
                <Route path="/guia" element={<HealthGuide />} />
                <Route path="/diagnostico" element={<Diagnosis />} />
                <Route path="/legal/:page" element={<Legal />} />
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/asesor" element={<Advisor />} />
                <Route path="/gear" element={<Gear />} />
                <Route path="/peso" element={<HorseWeight />} />
                <Route path="/cuadra" element={<Stable />} />
                <Route path="/concursos" element={<Competitions />} />
                <Route path="/alertas" element={<Alerts />} />
                <Route path="/mercado" element={<Market key="caballo" />} />
                <Route path="/mercado/guarnicioneria" element={<Market key="accesorio" kind="accesorio" />} />
                <Route path="/mercado/vans" element={<Market key="vehiculo" kind="vehiculo" />} />
                <Route path="/mercado/nuevo" element={<ListingForm />} />
                <Route path="/mercado/:id" element={<ListingPage />} />
                <Route path="/mercado/:id/editar" element={<ListingForm />} />
                <Route path="/adopcion" element={<AdoptList />} />
                <Route path="/adopcion/publicar" element={<AdoptForm />} />
                <Route path="/adopcion/panel" element={<AdoptPanel />} />
                <Route path="/adopcion/normas" element={<AdoptRules />} />
                <Route path="/adopcion/mensajes/:tid" element={<AdoptChat />} />
                <Route path="/adopcion/:id" element={<AdoptPet />} />
                <Route path="/adopcion/:id/editar" element={<AdoptForm />} />
                <Route path="/recuperar" element={<ResetPassword />} />
                <Route path="/restablecer" element={<ResetPassword />} />
                <Route path="/antidopaje" element={<Antidoping />} />
                <Route path="/cartel-app" element={<AppPoster />} />
                <Route path="/club" element={<ClubHub />} />
                <Route path="/club/pago" element={<ClubPayResult />} />
                <Route path="/club/concurso/:id" element={<CompetitionPage />} />
                <Route path="/club/:id" element={<ClubPage />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="*" element={<Home />} />
              </Routes>
              </RouteBoundary>
            </main>
            <Footer />
            <CartDrawer />
            <ChatWidget />
            <Toaster position="top-center" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
